import React, {
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Button, Form, FormGroup } from "reactstrap";
import VideoCardNewMission from "../VideoCardNewMission/VideoCardNewMission";
import useSubtitleValidationStore from "../../store/useSubtitleValidationStore";
import { useDropzone } from "react-dropzone";
import { MdOutlineCloudUpload } from "react-icons/md";

const ModalUploadVideoNewMission = forwardRef(
  ({ updateMissions, lightUpload = false }, ref) => {
    const { isAnyValidating, getValidatingCount, finishValidating } =
      useSubtitleValidationStore();

    const [videos, setVideos] = useState([]);
    const [isUploading, setIsUploading] = useState(false);

    const videoRefs = useRef([]);
    const childCancelTokens = useRef([]);

    useImperativeHandle(ref, () => ({
      cancelUploads: () => {
        cancelRequests();
      },
    }));

    const handleBatchUpload = async () => {
      const BATCH_SIZE = 5;
      const refs = videoRefs.current.filter(Boolean);
      const refsToUpload = refs.filter((ref) => !ref.uploaded());

      if (refsToUpload.length === 0) return;

      setIsUploading(true);

      try {
        for (let i = 0; i < refsToUpload.length; i += BATCH_SIZE) {
          const batch = refsToUpload.slice(i, i + BATCH_SIZE);
          await Promise.all(batch.map((ref) => ref.upload()));
        }
      } catch (e) {
        console.error(e);
      } finally {
        setIsUploading(false);
      }
    };

    const handleUpload = async (files) => {
      const videoFiles = files;
      if (!videoFiles || videoFiles.length === 0) return;

      setVideos((prev) => {
        const existingNames = new Set(prev.map((video) => video.name));

        const newUniqueVideos = videoFiles.filter(
          (file) => !existingNames.has(file.name)
        );

        return [...prev, ...newUniqueVideos.sort((a, b) => b.size - a.size)];
      });
    };

    const registerCancelToken = useCallback((token) => {
      childCancelTokens.current = [...childCancelTokens.current, token];
    }, []);

    const cancelRequests = useCallback(() => {
      childCancelTokens.current.forEach((token) => {
        token.cancel("Upload canceled from parent");
      });
      childCancelTokens.current = [];

      setVideos([]);
      updateMissions((prev) => ++prev);

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateMissions]);

    const removeVideo = (videoName) => {
      finishValidating(videoName);
      setVideos((prev) => prev.filter((file) => file.name !== videoName));
    };

    const { getRootProps, getInputProps } = useDropzone({
      onDrop: (acceptedFiles) => {
        handleUpload(acceptedFiles);
      },
      accept: lightUpload
        ? { "video/mp4": [".mp4"] }
        : { "video/x-insv": [".insv"] },
      multiple: true,
    });

    return (
      <Form style={{ padding: 0 }}>
        <FormGroup>
          {!isUploading && (
            <div
              {...getRootProps()}
              style={{
                border: "2px dashed #ccc",
                borderRadius: "8px",
                padding: "0 20px",
                textAlign: "center",
                background: "#f9f9f9",
                cursor: "pointer",
                width: lightUpload ? "432px" : "100%",
                height: "80px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "4px",
                marginBottom: videos.length > 0 ? "1rem" : "0",
              }}
            >
              <input {...getInputProps()} />
              <MdOutlineCloudUpload size={24} />
              <p style={{ margin: 0 }}>Arraste vídeos aqui para adicionar!</p>
            </div>
          )}

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              overflowY: "scroll",
              height: videos.length > 0 ? "350px" : "",
            }}
          >
            {videos?.map((video, index) => {
              return (
                <VideoCardNewMission
                  ref={(el) => (videoRefs.current[index] = el)}
                  video={video}
                  key={video.name}
                  registerCancelToken={registerCancelToken}
                  removeVideo={removeVideo}
                />
              );
            })}
          </div>

          {videos.length !== 0 && (
            <div>
              <Button
                outline
                className="col-12 mt-3"
                onClick={() => cancelRequests()}
                disabled={!isUploading}
              >
                Cancelar
              </Button>

              <Button
                outline
                className="col-12 mt-3"
                disabled={isUploading || isAnyValidating()}
                onClick={handleBatchUpload}
              >
                {isAnyValidating()
                  ? `Validando ${getValidatingCount()} videos...`
                  : "Enviar"}
              </Button>
            </div>
          )}
        </FormGroup>
      </Form>
    );
  }
);

export default ModalUploadVideoNewMission;
